import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {boolean} */
  #skipNextHistoryOpen = false;

  /** @type {number | null} */
  #pageScrollY = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    this.#pageScrollY = this.#getPageScrollY();

    if (!isMobileBreakpoint()) return;

    if (this.#skipNextHistoryOpen) {
      return;
    }

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    const scrollY = this.#pageScrollY ?? this.#getPageScrollY();
    this.#pageScrollY = null;
    this.#skipNextHistoryOpen = false;
    this.#historyAbortController?.abort();

    if (history.state?.cartDrawerOpen) {
      this.#returnFromDrawerHistory(scrollY);
    } else {
      this.#restorePageScroll(scrollY);
    }
  };

  /**
   * Removes the drawer-only history entry without allowing the browser to
   * replace the page position saved when the drawer opened.
   * @param {number} scrollY
   */
  #returnFromDrawerHistory(scrollY) {
    const previousScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    let restored = false;

    const restore = () => {
      if (restored) return;
      restored = true;
      this.#restorePageScroll(scrollY);
      history.scrollRestoration = previousScrollRestoration;
    };

    window.addEventListener('popstate', restore, { once: true });
    history.back();

    // Safety fallback for browsers that delay or suppress popstate.
    window.setTimeout(restore, 250);
  }

  /**
   * Restores after focus return, dialog cleanup and layout have all completed.
   * @param {number} scrollY
   */
  #restorePageScroll(scrollY) {
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
      });
    });
  }

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    const shouldOpenDrawer =
      this.hasAttribute('auto-open') || event.detail?.data?.source === 'product-form-component';

    if (shouldOpenDrawer && !event.detail?.data?.didError) {
      this.#pageScrollY = this.#getPageScrollY();
      this.#skipNextHistoryOpen = true;
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  #getPageScrollY() {
    const bodyTop = document.body.style.top;
    if (document.body.style.position === 'fixed' && bodyTop) {
      const fixedScrollY = Math.abs(parseInt(bodyTop, 10));
      if (!Number.isNaN(fixedScrollY)) return fixedScrollY;
    }

    return window.scrollY || window.pageYOffset || 0;
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
